import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, BookOpen } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SmoothScroll from "@/components/SmoothScroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogList() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Journal — Calm Dentistry | Dental Health Articles, Andheri East & Powai";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Dental health articles from Calm Dentistry — root canals, aligners, whitening, paediatric care and preventive tips. Written by Dr Raksha Shetty, Andheri East & Powai.");
    axios.get(`${API}/articles`).then((r) => { setArticles(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <main className="min-h-screen">
      <SmoothScroll />
      <Navbar />

      <section className="pt-[130px] md:pt-[150px] pb-16 md:pb-24 soft-radial">
        <div className="max-w-[1440px] mx-auto px-5 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 mb-8 text-[13px] text-[hsl(var(--primary-dark))] link-sweep" data-testid="blog-back-home">
            <ArrowLeft size={14} /> Back to home
          </Link>

          <div className="grid grid-cols-12 gap-4 md:gap-6 items-end mb-10 md:mb-20">
            <div className="col-span-12 md:col-span-8">
              <div className="eyebrow mb-4">The Journal</div>
              <h1 className="font-display text-4xl md:text-7xl leading-[1.02] tracking-[-0.03em]">
                Notes from a <br /><span className="text-[hsl(var(--primary))]">calmer chair.</span>
              </h1>
            </div>
            <div className="col-span-12 md:col-span-4">
              <p className="text-[hsl(var(--ink-soft))] leading-relaxed">
                Honest, evidence-based writing on dentistry — from Dr Raksha Shetty and the team at Calm Dentistry.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-24 text-[hsl(var(--ink-soft))]" data-testid="blog-loading">Loading…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
              {articles.map((a, i) => (
                <motion.article
                  key={a.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: (i % 3) * 0.06 }}
                  data-testid={`article-card-${a.slug}`}
                >
                  <Link to={`/blog/${a.slug}`} className="card-soft block overflow-hidden group">
                    <div className="aspect-[16/11] overflow-hidden bg-[hsl(var(--bg-2))]">
                      <img src={a.cover} alt={a.title} className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(.2,.7,.2,1)] group-hover:scale-[1.06]" loading="lazy" />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-[12px] text-[hsl(var(--ink-soft))]">
                        <span>{formatDate(a.published_at)}</span><span>·</span>
                        <BookOpen size={13}/> <span>{a.reading_time_min} min read</span>
                      </div>
                      <h2 className="font-display text-xl md:text-2xl leading-[1.15] tracking-[-0.01em] mt-3">
                        {a.title}
                      </h2>
                      <p className="mt-3 text-[13px] text-[hsl(var(--ink-soft))] line-clamp-3 leading-relaxed">
                        {a.excerpt}
                      </p>
                      <div className="mt-5 pt-4 border-t border-[hsl(var(--line))]/70 flex items-center justify-between text-[hsl(var(--primary-dark))]">
                        <span className="text-[12px] font-medium">Read article</span>
                        <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return ""; }
}
